import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Sidebar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import MyAssetsTable from "../../components/operationsAssociate/MyAssetsTable"; // Updated Table
import ReturnAssetModal from "../../components/operationsAssociate/ReturnAssetModal";
import ViewAssetModal from "../../components/operationsAssociate/ViewAssetModal";
import { Navigate } from "react-router-dom";

const API_URL = "https://ims.holdthechild.net";

// Assuming ProtectedRoute is defined elsewhere or in App.js
export const ProtectedRoute = ({ children }) => {
    const token = localStorage.getItem("token");
    return token ? children : <Navigate to="/" />;
};

const HrAssetsPage = () => {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [loggedInStaffId, setLoggedInStaffId] = useState(null);

    // Fetch logged-in user ID from localStorage
    useEffect(() => {
        const userStr = localStorage.getItem("user");
        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                // Adjust based on how your user object stores the ID (id or _id)
                if (user && user.id) {
                    setLoggedInStaffId(user.id);
                } else if (user && user._id) {
                    setLoggedInStaffId(user._id);
                } else {
                    toast.error("User ID not found in session.");
                }
            } catch (e) {
                console.error("Error parsing user from localStorage", e);
            }
        } else {
            toast.error("User not logged in. Redirecting...");
        }
    }, []);

    const fetchMyAssets = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("Authentication token not found.");
            }
            const response = await axios.get(`${API_URL}/api/assets/my-assets`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAssets(response.data.data || []);
        } catch (err) {
            console.error("Failed to fetch my assets:", err);
            setError("Unable to fetch your assets. Please try again later.");
            toast.error(err.response?.data?.message || "Server error while loading assets. 😢");
        } finally {
            setLoading(false);
        }
    };

    // Logout handler
    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user"); // Ensure user is cleared too
        window.location.href = "/";
    };

    useEffect(() => {
        // Only fetch if we have a valid staff ID
        if (loggedInStaffId) {
            fetchMyAssets();
        }
    }, [loggedInStaffId]);

    const handleOpenReturnModal = (asset) => {
        setSelectedAsset(asset);
        setIsReturnModalOpen(true);
    };

    const handleCloseReturnModal = () => {
        setIsReturnModalOpen(false);
        setSelectedAsset(null);
        fetchMyAssets(); // Refresh data after returning
    };

    const handleOpenViewModal = (asset) => {
        setSelectedAsset(asset);
        setIsViewModalOpen(true);
    };

    const handleCloseViewModal = () => {
        setIsViewModalOpen(false);
        setSelectedAsset(null);
    };

    return (
        <div className="flex min-h-screen w-full bg-gray-50 font-sans">
            <Sidebar onLogout={handleLogout} />
            <div className="flex-1 relative overflow-y-auto md:ml-64">
                <div className="fixed top-0 left-0 md:left-64 right-0 z-10">
                    <TopBar />
                </div>
                <main className="pt-20 px-4 md:px-6 pb-8">
                    <div className="max-w-7xl mx-auto">
                        <h1 className="text-2xl font-bold text-gray-800 mb-2">
                            My Assigned Assets
                        </h1>
                        <p className="text-gray-500 mb-6 text-sm">
                            Manage assets assigned directly to you or where you are listed as the custodian.
                        </p>

                        {/* Loading State */}
                        {loading ? (
                            <div className="flex flex-col items-center justify-center p-12 bg-white rounded-lg shadow-sm border border-gray-100">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mb-4"></div>
                                <p className="text-gray-500 font-medium">Loading your assets...</p>
                            </div>
                        ) : (
                            /* 
                                UPDATED: Passing refreshAssets={fetchMyAssets} 
                                This allows the table to trigger a data refresh after Accept/Reject actions.
                            */
                            <MyAssetsTable
                                assets={assets}
                                onReturn={handleOpenReturnModal}
                                onView={handleOpenViewModal}
                                loggedInStaffId={loggedInStaffId}
                                refreshAssets={fetchMyAssets}
                            />
                        )}
                    </div>
                </main>
            </div>

            {/* Modals */}
            {isReturnModalOpen && selectedAsset && (
                <ReturnAssetModal
                    asset={selectedAsset}
                    closeModal={handleCloseReturnModal}
                />
            )}
            {isViewModalOpen && selectedAsset && (
                <ViewAssetModal
                    asset={selectedAsset}
                    closeModal={handleCloseViewModal}
                />
            )}
        </div>
    );
};

export default HrAssetsPage;